/* AquaCurve - Sticky CTA Thumbnail + Price Sync (Ignite)
 * Version: 2026-01-09-v6
 *
 * Key fix (v6):
 *  - Fix "thumb lags one step" when switching Configuration cards
 *  - Thumb source priority:
 *      (1) Active Configuration card thumbnail (instant, no gallery delay)
 *      (2) Variant featured image from JSON
 *      (3) Active media/gallery fallback
 *
 * Display rule:
 *  - For display (thumb/price), ALWAYS prefer URL ?variant=
 *
 * Safety:
 *  - No polling / no intervals
 *  - RAF throttle + limited delayed retries only
 */
(() => {
  'use strict';

  const VERSION = '2026-01-09-v6';

  const STICKY_SEL = 'div[id^="sticky-cta-template--"][id$="__main"].sticky-cta';
  const INFO_SEL = '.sticky-cta__info';
  const THUMB_CLASS = 'ac-sticky-thumb';
  const PRICE_SEL = '.sticky-cta__price';

  let rafQueued = false;
  let lastUrlVariant = null;

  // timers (limited retries only)
  let t0 = null, t1 = null, t2 = null;

  function $(sel, root = document) {
    return root.querySelector(sel);
  }

  function $all(sel, root = document) {
    return root.querySelectorAll(sel);
  }

  function getStickyEl() {
    return $(STICKY_SEL);
  }

  function scheduleUpdate() {
    if (rafQueued) return;
    rafQueued = true;
    requestAnimationFrame(() => {
      rafQueued = false;
      updateStickyThumbAndPrice();
    });
  }

  // limited delayed retries (no polling)
  function scheduleUpdateWithDelays() {
    scheduleUpdate();

    clearTimeout(t0); clearTimeout(t1); clearTimeout(t2);
    t0 = setTimeout(scheduleUpdate, 0);
    t1 = setTimeout(scheduleUpdate, 80);
    t2 = setTimeout(scheduleUpdate, 220);
  }

  function getVariantIdFromUrl() {
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get('variant');
      return v ? String(v) : null;
    } catch (_) {
      return null;
    }
  }

  function getVariantIdFromInput() {
    const idInput = $('form[action*="/cart/add"] input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);
    return null;
  }

  // IMPORTANT: display should follow URL first (config cards update URL first)
  function getVariantIdForDisplay() {
    return getVariantIdFromUrl() || getVariantIdFromInput();
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (_) {
      return null;
    }
  }

  function findVariantInJson(obj, variantId) {
    if (!obj) return null;

    if (Array.isArray(obj)) {
      return obj.find(v => String(v && v.id) === String(variantId)) || null;
    }

    const candidates = [];
    if (obj.variants) candidates.push(obj.variants);
    if (obj.product && obj.product.variants) candidates.push(obj.product.variants);

    for (const arr of candidates) {
      if (Array.isArray(arr)) {
        const hit = arr.find(v => String(v && v.id) === String(variantId));
        if (hit) return hit;
      }
    }
    return null;
  }

  function getVariantData(variantId) {
    // 1) ShopifyAnalytics meta (often fastest)
    const metaVariants = window.ShopifyAnalytics?.meta?.product?.variants;
    if (Array.isArray(metaVariants)) {
      const hit = metaVariants.find(v => String(v && v.id) === String(variantId));
      if (hit) return hit;
    }

    // 2) Common JSON script nodes
    const scriptSelectors = [
      'product-info script[type="application/json"][data-selected-variant]',
      'product-info script[type="application/json"][data-product-variants]',
      'product-info script[type="application/json"]',
      'variant-selects script[type="application/json"]',
      'variant-radios script[type="application/json"]',
      'variant-picker script[type="application/json"]',
      'script[type="application/json"][data-product-json]',
      'script[type="application/json"][id*="ProductJson"]'
    ];

    for (const sel of scriptSelectors) {
      const nodes = $all(sel);
      for (const node of nodes) {
        const obj = safeJsonParse(node.textContent || '');
        const hit = findVariantInJson(obj, variantId);
        if (hit) return hit;
      }
    }

    return null;
  }

  function normalizeImgUrl(src) {
    if (!src) return '';
    let s = String(src).trim();
    if (!s) return '';
    if (s.startsWith('//')) s = 'https:' + s;
    if (s.startsWith('/')) s = window.location.origin + s;
    return s;
  }

  function withWidthParam(src, width) {
    try {
      const u = new URL(src);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (_) {
      return src;
    }
  }

  function extractVariantImageUrl(variant) {
    if (!variant) return '';
    const a =
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_media?.preview_image?.src ||
      variant.featured_media?.preview_image?.url ||
      variant.image?.src ||
      variant.image?.url;

    return normalizeImgUrl(a || '');
  }

  function getActiveMediaImageUrlFallback() {
    const candidates = [
      '.product__media-item.is-active img',
      '.product__media-item[aria-current="true"] img',
      '.product__media img',
      '.media-gallery img[aria-current="true"]',
      '.media-gallery img'
    ];

    for (const sel of candidates) {
      const img = $(sel);
      const src = img?.currentSrc || img?.src;
      if (src) return normalizeImgUrl(src);
    }
    return '';
  }

  function ensureThumbImg(sticky) {
    const info = sticky.querySelector(INFO_SEL);
    if (!info) return null;

    let wrap = info.querySelector('.' + THUMB_CLASS);
    if (!wrap) {
      wrap = document.createElement('span');
      wrap.className = THUMB_CLASS;
      wrap.setAttribute('aria-hidden', 'true');

      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';

      wrap.appendChild(img);
      info.insertBefore(wrap, info.firstChild);
    }

    return wrap.querySelector('img');
  }

  /**
   * IMPORTANT FIX:
   * Get thumbnail from the currently selected Configuration card itself (instant, no gallery delay).
   * This prevents "thumb lags one step".
   */
  function getConfigCardThumbUrl() {
    // Try likely roots of configuration cards
    const roots = [
      document.querySelector('.variant-models__grid'),
      document.querySelector('.variant-models'),
      document.querySelector('variant-models'),
      document.querySelector('[data-variant-models]'),
      document.querySelector('[role="radiogroup"]')
    ].filter(Boolean);

    let active = null;

    for (const root of roots) {
      active =
        root.querySelector('[role="radio"][aria-checked="true"]') ||
        root.querySelector('[aria-checked="true"]') ||
        root.querySelector('.is-active, .active, .selected') ||
        null;

      if (active) break;
    }

    if (!active) return '';

    const img =
      active.querySelector('img') ||
      active.querySelector('picture img') ||
      null;

    if (!img) return '';

    const src = img.currentSrc || img.src || '';
    return normalizeImgUrl(src);
  }

  function setThumb(imgEl, variantId, rawSrc) {
    if (!imgEl) return;

    const src = rawSrc ? withWidthParam(normalizeImgUrl(rawSrc), 120) : '';
    const prev = imgEl.getAttribute('data-ac-src') || '';

    if (src && src === prev) {
      // already correct
      imgEl.style.display = 'block';
      imgEl.setAttribute('data-vid', String(variantId));
      return;
    }

    if (src) {
      imgEl.style.display = 'block';
      imgEl.setAttribute('data-vid', String(variantId));
      imgEl.setAttribute('data-ac-src', src);
      imgEl.src = src;
    } else {
      imgEl.removeAttribute('src');
      imgEl.style.display = 'none';
      imgEl.removeAttribute('data-vid');
      imgEl.removeAttribute('data-ac-src');
    }
  }

  function formatMoneyCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';

    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(n, moneyFormat);
    }

    const dollars = n / 100;
    return dollars.toLocaleString(undefined, {
      style: 'currency',
      currency: (window.Shopify?.currency?.active || 'USD')
    });
  }

  function updateStickyThumbAndPrice() {
    const sticky = getStickyEl();
    if (!sticky) return;

    const variantId = getVariantIdForDisplay();
    if (!variantId) return;

    const variant = getVariantData(variantId);

    // ---- Thumb ----
    const imgEl = ensureThumbImg(sticky);
    if (imgEl) {
      // Priority 1: Configuration card thumbnail (instant)
      let src = getConfigCardThumbUrl();

      // Priority 2: variant featured image from JSON
      if (!src) src = extractVariantImageUrl(variant);

      // Priority 3: active media fallback (may lag, so last resort)
      if (!src) src = getActiveMediaImageUrlFallback();

      setThumb(imgEl, variantId, src);
    }

    // ---- Price ----
    const priceEl = sticky.querySelector(PRICE_SEL);
    if (priceEl && variant && (variant.price != null)) {
      const txt = formatMoneyCents(variant.price);
      if (txt && priceEl.textContent !== txt) priceEl.textContent = txt;
    }
  }

  function hookHistory() {
    const _push = history.pushState;
    const _replace = history.replaceState;

    function checkUrlVariantAndUpdate() {
      const v = getVariantIdFromUrl();
      if (v && v !== lastUrlVariant) {
        lastUrlVariant = v;
        scheduleUpdateWithDelays();
      }
    }

    history.pushState = function (...args) {
      const ret = _push.apply(this, args);
      checkUrlVariantAndUpdate();
      return ret;
    };

    history.replaceState = function (...args) {
      const ret = _replace.apply(this, args);
      checkUrlVariantAndUpdate();
      return ret;
    };

    window.addEventListener('popstate', checkUrlVariantAndUpdate);

    // init
    lastUrlVariant = getVariantIdFromUrl();
  }

  function bindSync() {
    hookHistory();

    // Custom/Theme variant events
    document.addEventListener('variant:change', scheduleUpdateWithDelays, true);
    document.addEventListener('product:variant-change', scheduleUpdateWithDelays, true);

    // Any picker change
    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (!t) return;
        if (t.closest('variant-radios, variant-selects, variant-picker, variant-models')) {
          scheduleUpdateWithDelays();
        }
      },
      true
    );

    // Config cards click (critical for Configuration lag issue)
    document.addEventListener(
      'click',
      (e) => {
        const el = e.target;
        if (!el) return;
        if (el.closest('.variant-models__grid, .variant-models, variant-models, [data-variant-models], [role="radiogroup"]')) {
          scheduleUpdateWithDelays();
        }
      },
      true
    );

    // id input attribute changes (some scripts use setAttribute)
    const idInput = $('form[action*="/cart/add"] input[name="id"]');
    if (idInput) {
      const mo = new MutationObserver(() => scheduleUpdateWithDelays());
      mo.observe(idInput, { attributes: true, attributeFilter: ['value'] });
      idInput.addEventListener('change', scheduleUpdateWithDelays, true);
      idInput.addEventListener('input', scheduleUpdateWithDelays, true);
    }

    // initial
    scheduleUpdateWithDelays();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSync, { once: true });
  } else {
    bindSync();
  }

  window.__AC_STICKY_THUMB__ = { VERSION, scheduleUpdate: scheduleUpdateWithDelays };
})();
